import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios'; 
import { GET_CUSTOMER_INVOICE } from '../common/urls'; 
import NewWindow from "./NewWindow"; 
import * as pdfjsLib from 'pdfjs-dist/build/pdf';
import PDFWorker from 'pdfjs-dist/build/pdf.worker.entry';
import { useTranslation } from "react-i18next"; 

pdfjsLib.GlobalWorkerOptions.workerSrc = PDFWorker;

const InvoiceViewer = ({ orderId, actionType, onCloseAction }) => {
  const [pdfData, setPdfData] = useState(null); 
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const canvasContainerRef = useRef(null);
  const { t } = useTranslation(); 

  // Clear cache if the orderId changes
  useEffect(() => {
    setPdfData(null);
    setError(null);
    setShowPopup(false);
  }, [orderId]);

  // Watch for actionType changes from the parent component
  useEffect(() => {
    console.log("InvoiceViewer useEffect orderId" , orderId)
    if (!orderId || !actionType) return;

    const handleAction = async () => {
      setError(null);
      
      // If we already have the cached PDF data
      if (pdfData) {
        if (actionType === 'view') {
          setShowPopup(true);
        }
        if (actionType === 'download') {
          triggerDownload(pdfData);
          onCloseAction(); // Safely reset parent state for downloads instantly
        }
        return;
      }

      //loadingTrue = () => setLoading(true); // preserved internal logic mapping
      setLoading(true);
      try {
        const url = `${GET_CUSTOMER_INVOICE}?Id=${orderId}`;
        const response = await axios.get(url, { responseType: 'arraybuffer' });
        setPdfData(response.data);

        if (actionType === 'view') {
          setShowPopup(true);
        } else if (actionType === 'download') {
          triggerDownload(response.data);
          onCloseAction(); // Safely reset parent state for downloads instantly
        }
      } catch (err) {
        console.error("Error fetching PDF:", err);
        setError(err.message || "Failed to load live invoice data.");
        onCloseAction(); // Reset parent on error so they can try again
      } finally {
        setLoading(false);
      }
    };

    handleAction();
  }, [actionType, orderId, pdfData]);

  // Handles rendering the canvas once popup opens and data is ready
  useEffect(() => {
    if (showPopup && pdfData) {
      const timer = setTimeout(() => {
        renderPdf(pdfData);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [showPopup, pdfData]);

  const renderPdf = async (arrayBuffer) => {
    if (!canvasContainerRef.current) return;
    canvasContainerRef.current.innerHTML = ''; 

    try {
      const typedArray = new Uint8Array(arrayBuffer);
      const loadingTask = pdfjsLib.getDocument({ data: typedArray });
      const pdf = await loadingTask.promise;

      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const viewport = page.getViewport({ scale: 1.3 });
        
        const canvas = document.createElement('canvas');
        canvas.style.marginBottom = '20px';
        canvas.style.display = 'block';
        canvas.style.boxShadow = '0 4px 10px rgba(0,0,0,0.15)';
        
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        canvasContainerRef.current.appendChild(canvas);

        await page.render({
          canvasContext: context,
          viewport: viewport
        }).promise;
      }
    } catch (err) {
      console.error("Canvas rendering failed:", err);
      if (canvasContainerRef.current) {
        // Appended translated dynamic error handling safely without altering logic
        canvasContainerRef.current.innerHTML = `<p style="color:red;padding:20px;">${t("Failed to display PDF")}: ${err.message}</p>`;
      }
    }
  };

  const triggerDownload = (arrayBuffer) => {
    const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
    const fileUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = `Invoice_${orderId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(fileUrl);
  };

  return (
    <>
      {loading && (
        <div style={{ position: 'fixed', top: 10, right: 10, background: '#fff', padding: '10px', zIndex: 9999, borderRadius: '4px', boxShadow: '0 2px 5px rgba(0,0,0,0.2)' }}>
          ⏳ {t("Processing Invoice...")}
        </div>
      )}
      {error && <p style={{ color: 'red', margin: '10px 0' }}>❌ {t("Error")}: {error}</p>}

      <NewWindow
        open={showPopup}
        onClose={() => {
          setShowPopup(false);
          onCloseAction(); // Clears state in parent ONLY when modal is closed
        }}
        title={t("Invoice Viewer")}
        fitContent={true}
        customModalClass="custom-modal"
        blockScroll={false}
      >
        <div 
          ref={canvasContainerRef}
          style={{ 
            width: '750px', 
            height: '650px', 
            overflowY: 'auto', 
            backgroundColor: '#525659', 
            padding: '20px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <p style={{ color: '#fff' }}>{t("Assembling document elements dynamically...")}</p>
        </div>
      </NewWindow>
    </>
  );
};

export default InvoiceViewer;